/** Shared building blocks for Customer 360°. */
import { useNavigate } from "react-router-dom";
import StatusBadge from "@/components/app/StatusBadge";
import { ExternalLink } from "lucide-react";

export const inr = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
export const d = (s) => (s ? String(s).slice(0, 10) : "—");
export const dt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

export const Section = ({ title, count, children, testId, right }) => (
  <section className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-600">{title}</h3>
      {count != null && <span className="font-mono text-[10px] text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5">{count}</span>}
      <div className="ml-auto flex items-center gap-2">{right}</div>
    </div>
    <div className="p-3 sm:p-4">{children}</div>
  </section>
);

export const Stat = ({ label, value, tone, testId, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testId}
    className={`text-left rounded-lg border border-slate-200 bg-white px-3.5 py-3 ${onClick ? "hover:border-slate-300 hover:shadow-sm" : "cursor-default"}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">{label}</div>
    <div className={`font-heading text-xl sm:text-2xl font-bold tracking-tight mt-0.5 ${tone === "bad" ? "text-rose-700" : tone === "good" ? "text-emerald-700" : tone === "warn" ? "text-amber-600" : "text-slate-900"}`}>{value ?? "—"}</div>
  </button>
);

export const KV = ({ k, v, mono }) => (
  <div className="flex gap-3 py-1 border-b border-slate-50 last:border-0 text-[13px]">
    <span className="w-36 shrink-0 text-slate-500">{k}</span>
    <span className={`text-slate-900 break-words min-w-0 ${mono ? "font-mono text-[12px]" : ""}`}>{v || <span className="text-slate-300">—</span>}</span>
  </div>
);

/** Generic drill-down table. cols: [{key,label,render?,mono?,className?}] */
export function RecordTable({ rows, cols, empty = "Nothing here yet.", testId, rowRoute = (r) => r.route, rowKey = (r) => r.id }) {
  const navigate = useNavigate();
  if (!rows?.length) return <div className="text-xs text-slate-400 py-2">{empty}</div>;
  return (
    <div className="overflow-x-auto -mx-3 sm:mx-0" data-testid={testId}>
      <table className="w-full text-[13px] min-w-[560px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
            {cols.map((c) => <th key={c.key} className={`px-3 py-1.5 font-semibold ${c.className || ""}`}>{c.label}</th>)}
            <th className="px-3 py-1.5 w-8" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const route = rowRoute(r);
            return (
              <tr key={rowKey(r)} data-testid={`${testId}-row-${rowKey(r)}`} onClick={() => route && navigate(route)}
                className={`border-t border-slate-100 ${route ? "cursor-pointer hover:bg-sky-50/40" : ""}`}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-2 align-top ${c.mono ? "font-mono text-[12px]" : ""} ${c.className || ""}`}>
                    {c.render ? c.render(r) : c.key === "status" ? (r.status ? <StatusBadge value={r.status} /> : "—") : (r[c.key] ?? "—")}
                  </td>
                ))}
                <td className="px-2 py-2 text-slate-300">{route && <ExternalLink className="h-3.5 w-3.5" />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
